import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  Tag,
  message,
  Popconfirm,
  Typography,
  Select
} from 'antd';
import {
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CrownOutlined,
  CheckCircleOutlined,
  UserOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Title } = Typography;
const { Option } = Select;

const UserManagement = ({ embedded = false }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize
      };
      if (searchText) {
        params.search = searchText;
      }

      const response = await api.get('/api/users', { params });

      if (response.data.status === 'success') {
        setUsers(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total
        }));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, searchText]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    });
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
  };

  const showCreateModal = () => {
    setEditingUser(null);
    form.resetFields();
    // Set default values for new user
    form.setFieldsValue({
      role: 'user',
      is_active: true
    });
    setIsModalVisible(true);
  };

  const showEditModal = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      email: user.email,
      name: user.name,
      role: user.role || (user.is_admin ? 'admin' : 'user'),
      is_active: user.is_active
    });
    setIsModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingUser) {
        // Update user
        const response = await api.put(`/api/users/${editingUser.id}`, {
          name: values.name,
          role: values.role,
          is_active: values.is_active
        });

        if (response.data.status === 'success') {
          message.success('User updated successfully');
          fetchUsers();
          setIsModalVisible(false);
          form.resetFields();
        }
      } else {
        // Create user
        const response = await api.post('/api/users', {
          email: values.email,
          name: values.name,
          role: values.role || 'user'
        });

        if (response.data.status === 'success') {
          message.success('User created successfully');
          fetchUsers();
          setIsModalVisible(false);
          form.resetFields();
        }
      }
    } catch (error) {
      console.error('Error saving user:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to save user');
      }
    }
  };

  const handleDelete = async (userId) => {
    try {
      const response = await api.delete(`/api/users/${userId}`);

      if (response.data.status === 'success') {
        message.success('User deleted successfully');
        fetchUsers();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to delete user');
      }
    }
  };

  const columns = [
    {
      title: 'User Info',
      key: 'user_info',
      render: (_, record) => {
        const userRole = record.role || (record.is_admin ? 'admin' : 'user');
        const roleConfig = {
          admin: { color: 'blue', icon: <CrownOutlined />, label: 'Admin' },
          verifier: { color: 'green', icon: <CheckCircleOutlined />, label: 'Verifier' },
          user: { color: 'default', icon: <UserOutlined />, label: 'User' }
        };
        const config = roleConfig[userRole] || roleConfig.user;

        return (
          <div style={{ minWidth: 280 }}>
            {/* Name and Email */}
            <div style={{ display: 'flex', alignItems: 'start', gap: 8, marginBottom: 6 }}>
              <UserOutlined style={{ color: '#14B8A6', fontSize: '16px', marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: '14px', lineHeight: '1.4', marginBottom: 4 }}>
                  {record.name || 'No name'}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {record.email}
                </div>
              </div>
            </div>

            {/* Tags: Role and Status */}
            <div>
              <Tag color={config.color} icon={config.icon} style={{ marginRight: 4, marginBottom: 4 }}>
                {config.label}
              </Tag>
              <Tag color={record.is_active ? 'green' : 'red'} style={{ marginBottom: 4 }}>
                {record.is_active ? 'Active' : 'Inactive'}
              </Tag>
            </div>
          </div>
        );
      },
      width: '40%',
    },
    {
      title: 'Last Login',
      key: 'last_login',
      render: (_, record) => (
        <div style={{ minWidth: 140, fontSize: '12px' }}>
          <div style={{ color: '#666', marginBottom: 2 }}>Last Login</div>
          <div style={{ fontWeight: 500 }}>
            {record.last_login ? new Date(record.last_login).toLocaleString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            }) : 'Never'}
          </div>
        </div>
      ),
      width: '180px',
    },
    {
      title: 'Aksi',
      key: 'actions',
      fixed: 'right',
      width: 100,
      render: (_, record) => (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
            block
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              block
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const content = (
    <>
      {!embedded && <Title level={3} style={{ margin: '0 0 20px 0' }}>User Management</Title>}
      <div className="filters-row" style={{ justifyContent: 'space-between' }}>
        <Input.Search
          placeholder="Search by email or name"
          allowClear
          onSearch={handleSearch}
          prefix={<SearchOutlined />}
        />
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchUsers}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={showCreateModal}
          >
            Add User
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} users`
        }}
        onChange={handleTableChange}
      />

      <Modal
        title={editingUser ? 'Edit User' : 'Create New User'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        okText={editingUser ? 'Update' : 'Create'}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Please input email' },
              { type: 'email', message: 'Please input valid email' }
            ]}
          >
            <Input
              placeholder="user@example.com"
              disabled={!!editingUser}
            />
          </Form.Item>

          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please input name' }]}
          >
            <Input placeholder="Full name" />
          </Form.Item>

          <Form.Item
            label="Role"
            name="role"
            rules={[{ required: true, message: 'Please select a role' }]}
            tooltip="Assign role to this user"
          >
            <Select placeholder="Select role">
              <Option value="admin">
                <Space>
                  <CrownOutlined style={{ color: '#14B8A6' }} />
                  <span>Admin - Full system access</span>
                </Space>
              </Option>
              <Option value="verifier">
                <Space>
                  <CheckCircleOutlined style={{ color: '#14B8A6' }} />
                  <span>Verifier - Can verify content</span>
                </Space>
              </Option>
              <Option value="user">
                <Space>
                  <UserOutlined />
                  <span>User - Basic access</span>
                </Space>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Active Status"
            name="is_active"
            valuePropName="checked"
            tooltip="Active users can log in to the system"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card>
      {content}
    </Card>
  );
};

export default UserManagement;
